import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Range, RangeFocus, RangeKeyDict } from 'react-date-range'
import { Button, Flex, Popover, PopoverTrigger, Text, useDisclosure } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { addDays, format as formatDate, subDays } from 'date-fns'
import { Calendar, ChevronDown } from 'lucide-react'
import LocaleText from 'components/LocaleText'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { DateRangeContent } from './DateRangeContent'
import { formatTimestamp, stringTsToDate } from 'utils'
import colors from 'constants/colors'
import { isRtlSelector } from 'redux/selectors/general'
import strings from 'constants/strings'
import { useDateRangeLocale } from 'hooks/useDateRangeLocale'

interface IProps {
  onSelectDate: (fromDate: Date, toDate: Date) => void
  onResetFilter: VoidFunction
  maxRange?: number
  openPopoverTriggerLiteral: string
  bgOfTriggerPopover?: string
  selectButtonLiteral?: string
  resetButtonLiteral?: string
  open?: boolean
  close?: () => void
  style?: any
  minDate?: Date
  maxDate?: Date
  initialValues?: string[] | null
  initilaDateValues?: Date[] | null
  inputFormat?: string
  useCustomTranslations?: boolean
  useDetailedTrigger?: boolean
}

export const DateRangePopover = ({
  open,
  close,
  style,
  onSelectDate,
  onResetFilter,
  maxRange,
  bgOfTriggerPopover,
  openPopoverTriggerLiteral,
  resetButtonLiteral = 'archive_current_tasks',
  selectButtonLiteral = 'archive_select_date',
  maxDate,
  minDate,
  initialValues,
  initilaDateValues,
  useCustomTranslations = true,
  useDetailedTrigger = false,
}: IProps) => {
  const { isOpen, onClose, onToggle } = useDisclosure()
  const [isDefaultValueChanged, setIsDefaultValueChanged] = useState(false)
  const isRtl = useSelector(isRtlSelector)
  const [maxDateRange, setMaxDateRange] = useState<undefined | Date>(undefined)
  const [minDateRange, setMinDateRange] = useState<undefined | Date>(undefined)
  const { activeGroupID } = useSelector((state: RootState) => state.config)
  const theme = useSelector((state: RootState) => state.general.theme)
  const isUsLocale = theme?.is_support_us_date_format
  const dateRangeLocale = useDateRangeLocale(useCustomTranslations)

  const [state, setState] = useState<Range[]>([
    {
      startDate: minDate ? minDate : new Date(),

      endDate: maxDate ? maxDate : new Date(),
      key: 'selection',
    },
  ])

  useEffect(() => {
    if (initialValues && initialValues?.length > 0) {
      setState(() => [
        { ...state[0], startDate: stringTsToDate(initialValues[0]), endDate: stringTsToDate(initialValues[1]) },
      ])
    }
    if (initilaDateValues !== undefined) {
      if (initilaDateValues !== null) {
        setState(() => [{ ...state[0], startDate: initilaDateValues[0], endDate: initilaDateValues[1] }])
      } else {
        setState(() => [{ ...state[0], startDate: new Date(), endDate: new Date() }])
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, initilaDateValues])

  const handleFilter = () => {
    onSelectDate(state[0].startDate!, state[0].endDate!)
    onClose()
    close?.()
  }

  const resetRange = () => {
    setMinDateRange(() => undefined)
    setMaxDateRange(() => undefined)
  }

  const resetRangeState = () => {
    setState(() => [
      { ...state[0], startDate: minDate ? minDate : new Date(), endDate: maxDate ? maxDate : new Date() },
    ])
    resetRange()
    setIsDefaultValueChanged(false)
    onClose()
    close?.()
  }

  const resetFilter = () => {
    onResetFilter()
    resetRangeState()
  }

  const rangeFocusChangeHandler = (focusedDates: RangeFocus) => {
    if (!focusedDates[0] && focusedDates[1]) {
      setState((prevState: Range[]) => [{ ...prevState[0], endDate: prevState[0].startDate }])
    }
    if (!focusedDates[0] && !focusedDates[1]) {
      resetRange()
    }
  }

  const changeDateHandler = (value: RangeKeyDict) => {
    const newRange = { ...value.selection }
    if (newRange?.endDate) {
      const end = new Date(newRange.endDate)
      end.setHours(23, 59, 59, 999)
      newRange.endDate = end
    }
    setState([newRange])

    setIsDefaultValueChanged(true)
  }

  useEffect(() => {
    resetRangeState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupID])

  useEffect(() => {
    if (isDefaultValueChanged && maxRange) {
      setMaxDateRange(() => addDays(state[0].startDate!, maxRange))
      setMinDateRange(() => subDays(state[0].startDate!, maxRange))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state[0].startDate, isDefaultValueChanged])

  const popoverOpen = open !== undefined ? open : isOpen

  const format = isUsLocale ? strings.DATE_FORMATS.outputs.taskPeriodUS : strings.DATE_FORMATS.outputs.taskPeriod

  const text =
    initilaDateValues && initilaDateValues?.length > 0
      ? `${formatTimestamp(initilaDateValues[0].toISOString(), format)} - ${formatTimestamp(
          initilaDateValues[1].toISOString(),
          format
        )}`
      : ''

  const formatDetailedDate = useCallback(
    (date: Date) => {
      const detailedFormat = isUsLocale ? 'MMM d' : 'd MMM'
      return dateRangeLocale
        ? formatDate(date, detailedFormat, { locale: dateRangeLocale })
        : formatDate(date, detailedFormat)
    },
    [dateRangeLocale, isUsLocale]
  )

  const detailedText =
    initilaDateValues && initilaDateValues?.length > 0
      ? {
          from: formatDetailedDate(initilaDateValues[0]),
          to: formatDetailedDate(initilaDateValues[1]),
        }
      : null

  const handleClose = useCallback(() => {
    onClose()
    close?.()
  }, [onClose, close])

  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        handleClose()
      }
    }

    if (popoverOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [popoverOpen, handleClose])

  return (
    <div ref={popoverRef}>
      <Popover isOpen={popoverOpen} closeOnBlur onClose={handleClose}>
        {open === undefined && (
          <PopoverTrigger>
            <Button
              w="100%"
              px="5px"
              minW="fit-content"
              onClick={onToggle}
              fontWeight="500"
              dir={isRtl ? 'rtl' : 'ltr'}
              borderRadius="10px"
              background={bgOfTriggerPopover}
              fontSize="14px"
              border="1px solid"
              borderColor={colors.greyLight}
              pos="relative"
              _focus={{ outline: 'none' }}
            >
              {useDetailedTrigger ? (
                <Flex align="center" style={{ gap: '8px' }}>
                  {detailedText ? (
                    <>
                      <Calendar size={16} />
                      <span>{detailedText.from}</span>
                      <span>–</span>
                      <Calendar size={16} />
                      <span>{detailedText.to}</span>
                    </>
                  ) : (
                    <>
                      <Calendar size={16} />
                      <LocaleText text={openPopoverTriggerLiteral} />
                    </>
                  )}
                  <ChevronDown size={15} />
                </Flex>
              ) : (
                <Text>{text || <LocaleText text={openPopoverTriggerLiteral} />}</Text>
              )}
            </Button>
          </PopoverTrigger>
        )}

        <DateRangeContent
          style={style}
          onChange={changeDateHandler}
          ranges={state}
          className="byDateSelect"
          rangeFocusChange={rangeFocusChangeHandler}
          handleFilter={handleFilter}
          selectButtonLiteral={selectButtonLiteral}
          maxDateRange={maxDate ? maxDate : maxDateRange}
          minDateRange={minDate ? minDate : minDateRange}
          resetButtonLiteral={resetButtonLiteral}
          resetFilter={resetFilter}
          useCustomTranslations={useCustomTranslations}
        />
      </Popover>
    </div>
  )
}
